import React, { useState } from 'react';
import { Plus, RefreshCw } from 'lucide-react';
import Modal from './Modal';
import ImageUpload from './ImageUpload';
import NepaliTextInput from './NepaliTextInput';
import { showNotification } from './GlobalNotification';
import { useLanguage } from '../context/LanguageContext';

const AddMenuItemModal = ({ isOpen, onClose, onAddItem, loading }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    nameNepali: '',
    price: '',
    stock: '',
    includeInMenu: true,
    image: null,
    isInventoryItem: false,
    lowStockLimit: 5
  });

  const resetForm = () => {
    setFormData({
      name: '',
      nameNepali: '',
      price: '',
      stock: '',
      includeInMenu: true,
      image: null,
      isInventoryItem: false,
      lowStockLimit: 5
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.price || formData.price <= 0) {
      showNotification('error', t('Please provide valid item name and price'));
      return;
    }

    if (formData.stock !== '' && (isNaN(formData.stock) || formData.stock < 0)) {
      showNotification('error', t('Please provide a valid stock quantity'));
      return;
    }

    try {
      await onAddItem({
        name: formData.name.trim(),
        nameNepali: formData.nameNepali.trim(),
        price: Number(formData.price),
        stock: formData.stock === '' ? 0 : parseInt(formData.stock),
        includeInMenu: formData.includeInMenu,
        image: formData.image,
        isInventoryItem: formData.isInventoryItem,
        lowStockLimit: parseInt(formData.lowStockLimit) || 5
      });
      
      resetForm();
      onClose();
      showNotification('success', t('Item added successfully!'));
    } catch (error) {
      showNotification('error', t('Failed to add item'));
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('Add New Menu Item')}
      size="xl"
    >
      <div className="p-4 sm:p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
              {t('Basic Information')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Item name in both languages. English is required and is what
                  every screen falls back to; Nepali is optional and simply
                  replaces it when the till is switched to Nepali. */}
              <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Item Name (English)')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 sm:px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-cafe-500 focus:border-transparent transition-all duration-200 hover:border-gray-400"
                    placeholder={t('e.g., Milk Tea, Chicken Momo')}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    वस्तुको नाम (नेपाली){' '}
                    <span className="font-normal text-gray-400">{t('— optional')}</span>
                  </label>
                  <NepaliTextInput
                    defaultEnabled
                    value={formData.nameNepali}
                    onChange={(nameNepali) => setFormData({ ...formData, nameNepali })}
                    className="w-full px-3 sm:px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-cafe-500 focus:border-transparent transition-all duration-200 hover:border-gray-400"
                    placeholder="जस्तै: चिया, मःम"
                  />
                </div>
              </div>

              {/* Price and Stock Quantity - Same Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Price (Rs.)')} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-base">Rs</span>
                    <input
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="w-full pl-8 pr-3 sm:pr-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-cafe-500 focus:border-transparent transition-all duration-200 hover:border-gray-400"
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>

                {/* Stock Quantity */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Initial Stock Quantity')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    className="w-full px-3 sm:px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-cafe-500 focus:border-transparent transition-all duration-200 hover:border-gray-400"
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('Leave empty if not tracking stock')}</p>
                </div>
              </div>
            </div>

            {/* Menu Options */}
            <div className="bg-gray-50 p-4 rounded-xl">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="includeInMenu"
                  checked={formData.includeInMenu}
                  onChange={(e) => setFormData({ ...formData, includeInMenu: e.target.checked })}
                  className="h-5 w-5 text-cafe-600 focus:ring-cafe-500 border-gray-300 rounded transition-colors"
                />
                <label htmlFor="includeInMenu" className="text-sm font-medium text-gray-900">
                  {t('Include in customer menu')}
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1 ml-8">{t('Customers will be able to order this item')}</p>
            </div>
          </div>

          {/* Image Upload Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
              {t('Item Image')}
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('Upload Image (Optional)')}
              </label>
              <ImageUpload
                onImageSelect={(image) => setFormData({ ...formData, image })}
                currentImage={formData.image}
              />
              <p className="text-xs text-gray-500 mt-2">
                {t('Recommended: Square image, at least 300x300px for best quality')}
              </p>
            </div>
          </div>
          
          {/* Inventory Management Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
              {t('Inventory Management')}
            </h3>
            
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-4">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="isInventoryItem"
                  checked={formData.isInventoryItem}
                  onChange={(e) => setFormData({ ...formData, isInventoryItem: e.target.checked })}
                  className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5 transition-colors"
                />
                <div>
                  <label htmlFor="isInventoryItem" className="text-sm font-medium text-gray-900">
                    {t('Enable inventory tracking')}
                  </label>
                  <p className="text-xs text-gray-600 mt-1">
                    {t('Track stock levels and get low stock alerts for this item')}
                  </p>
                </div>
              </div>
              
              {formData.isInventoryItem && (
                <div className="ml-8 space-y-3 border-l-2 border-blue-300 pl-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('Low Stock Alert Threshold')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.lowStockLimit}
                      onChange={(e) => setFormData({ ...formData, lowStockLimit: e.target.value })}
                      className="w-full max-w-xs px-3 sm:px-4 py-2.5 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="5"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t("You'll receive notifications when stock falls below this number")}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 sm:flex-none sm:min-w-[140px] px-6 py-3.5 bg-gradient-to-r from-cafe-600 to-cafe-700 text-white rounded-xl hover:from-cafe-700 hover:to-cafe-800 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  <span>{t('Adding Item...')}</span>
                </>
              ) : (
                <>
                  <Plus className="h-5 w-5" />
                  <span>{t('Add Item')}</span>
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 sm:flex-none sm:min-w-[100px] px-6 py-3.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-medium transition-all duration-200 disabled:opacity-50 border border-gray-200 hover:border-gray-300"
            >
              {t('Cancel')}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AddMenuItemModal;